import { InputStepper, UI, HStack, Stack, VStack } from '@genial/components';
import { BoxBorder, Color, Spacing, Typography } from '@genial/design-tokens';
import { ResponsesWithdrawAssetResponse } from '@genial/services/dist/models';
import { Utils } from '@genial/utils';
import { Box, Button, TextArea } from 'native-base';
import React, { useState } from 'react';

import { FixedIncomeWithdrawAssetNormalized } from '../../../../../../store/redemptionSolicitation/types';
import { CardTitle } from '../../../../components/CardTitle';
import { FixedIncomeRules } from '../../../../components/FixedIncomeRules';
import { HighlightedInfos } from '../../../../components/HighlightedInfos';
import { InfoItem } from '../../../../components/InfoItem';
import { ProductCard } from '../../../../components/ProductCard';

import { fixedIncomeQuotationNotice } from './constants';

export declare namespace QuotationStepTypes {
  interface BaseProps {
    onCancel: () => void;
    asset: FixedIncomeWithdrawAssetNormalized;
    quote?: ResponsesWithdrawAssetResponse;
    quotationType: 'quotation' | 'directRedemption' | 'confirmation';
    redeemWithoutQuotation?: boolean;
    isExecuting?: boolean;
  }

  export interface QuotationStepProps extends BaseProps {
    quotationType: 'quotation';
    onConfirm: (quantitySelected: number) => void;
  }

  export interface DirectRedemptionStepProps extends BaseProps {
    quotationType: 'directRedemption';
    onConfirm: (quantity: number, channel: string, observation?: string) => void;
  }

  export interface ConfirmQuotationStepProps extends BaseProps {
    quotationType: 'confirmation';
    quote: ResponsesWithdrawAssetResponse;
    onConfirm: (channel: string, observation?: string) => void;
  }

  export type Props = QuotationStepProps | DirectRedemptionStepProps | ConfirmQuotationStepProps;
}

export const QuotationStep: React.FC<QuotationStepTypes.Props> = ({
  quotationType,
  onConfirm,
  onCancel,
  asset,
  isExecuting,
  quote,
  redeemWithoutQuotation,
}) => {
  const isQuotation = quotationType === 'quotation';
  const isDirectRedemption = quotationType === 'directRedemption';
  const isConfirmation = quotationType === 'confirmation';

  const [quantitySelected, setQuantitySelected] = useState(1);
  const [selectedOption, setSelectedOption] = useState('phone');
  const [textAreaInput, setTextAreaInput] = useState('');

  const handleButtonPress = () => {
    if (isQuotation) {
      onConfirm(quantitySelected);
    } else if (isDirectRedemption) {
      onConfirm(quantitySelected, selectedOption, textAreaInput);
    } else {
      onConfirm(selectedOption, textAreaInput);
    }
  };

  const getRedemptionValue = () => {
    if (isConfirmation) {
      return Utils.Mask.Money(quote?.netValue || 0);
    }
    const totalValue = asset.currentPriceNormalized * quantitySelected;
    return Utils.Mask.Money(totalValue);
  };

  const getIrValue = () => {
    if (quote) {
      return Utils.Mask.Money(quote.irValue);
    }
    return asset.irValueNormalized;
  };

  const getIofValue = () => {
    if (quote) {
      return Utils.Mask.Money(quote.iofValue);
    }
    return asset.iofValueNormalized;
  };

  return (
    <VStack space={Spacing['stack.xxxs']}>
      <ProductCard barColor="product.rf">
        <VStack space={Spacing['stack.xxxs']}>
          {!!isQuotation && !redeemWithoutQuotation && (
            <UI.Notice {...fixedIncomeQuotationNotice} />
          )}

          <CardTitle title={asset.productNameNormalized} />
          <InfoItem label="Taxa de Compra" value={asset.buyTaxNormalized} />
          <InfoItem label="Vencimento" value={asset.maturityDateFormatted} />
          <InfoItem label="Aportes" value={asset.contributionsTotalNormalized} />

          <HighlightedInfos
            infos={[
              { label: 'IR Previsto', value: getIrValue() },
              { label: 'IOF Previsto', value: getIofValue() },
            ]}
          />

          <InfoItem label="Rentabilidade no período" value={asset.rentabilityNormalized} />
          <InfoItem label="Saldo Bruto" value={asset.grossTotalValueNormalized} />
          <InfoItem label="Quantidade" value={asset.quantityNormalized} />
          <InfoItem label="Preço Unitário" value={asset.unitValueNormalized} />
          <InfoItem label="Taxa de Saida" value={asset.exitTaxNormalized} />
          <InfoItem label="Valor de Resgate" value={getRedemptionValue()} />

          {(!!isQuotation || !!redeemWithoutQuotation) && (
            <>
              <VStack space={Spacing['stack.quarck']} w="full">
                <UI.Text color="neutral.black" variant="semibold">
                  Quantidade desejada
                </UI.Text>

                <InputStepper
                  name="fixed-income-redemption-input"
                  stepperValue={1}
                  max={asset.quantity || Number.MAX_SAFE_INTEGER}
                  min={1}
                  keyboardType="numeric"
                  fontSize={Typography['fontsize.md']}
                  defaultValue={1}
                  onChange={(value) => setQuantitySelected(value)}
                  showErrorMessage={false}
                />
              </VStack>
              <FixedIncomeRules />
            </>
          )}

          {!isQuotation && (
            <>
              <UI.Text variant="semibold" color="neutral.black">
                Cliente confirmou o resgate
              </UI.Text>

              <VStack space={Spacing['stack.quarck']} w="full">
                <UI.Text color="neutral.darkest">Selecione o canal</UI.Text>
                <Stack bgColor={Color.background} borderRadius={BoxBorder.thick}>
                  <UI.Select
                    placeholder="Selecione o canal"
                    options={[
                      { label: 'Via Telefone', value: 'phone' },
                      { label: 'Via E-mail', value: 'email' },
                      { label: 'WhatsApp', value: 'whatsApp' },
                    ]}
                    selectedValue={selectedOption}
                    onValueChange={setSelectedOption}
                  />
                </Stack>
              </VStack>

              <VStack space={Spacing['stack.quarck']} w="full">
                <UI.Text color="neutral.darkest">Observação</UI.Text>
                <Box height={137}>
                  <TextArea
                    flex={1}
                    borderRadius={Spacing['inline.nano']}
                    backgroundColor={Color.background}
                    borderColor={'transparent'}
                    _focus={{
                      backgroundColor: 'transparent',
                      borderColor: Color['brand.primary.principal'],
                      borderWidth: 1,
                    }}
                    _hover={{ borderColor: Color['brand.primary.principal'] }}
                    value={textAreaInput}
                    placeholder="Existe alguma informação sobre essa autorização"
                    autoCompleteType={'off'}
                    onChangeText={setTextAreaInput}
                  />
                </Box>
              </VStack>
            </>
          )}
        </VStack>
      </ProductCard>

      <HStack
        alignSelf="end"
        space={Spacing['inline.xxxs']}
        w={Utils.Responsive.DesktopPhone({ phone: 'full', desktop: 'auto' })}
        justifyContent={Utils.Responsive.DesktopPhone({
          phone: 'space-between',
          desktop: 'inherit',
        })}>
        <UI.Button
          variant="secondary"
          onPress={onCancel}
          w={Utils.Responsive.DesktopPhone({ phone: '45%', desktop: 'auto' })}>
          Cancelar
        </UI.Button>
        <Button
          variant={isQuotation ? 'primary' : 'callToAction'}
          onPress={handleButtonPress}
          isDisabled={false}
          isLoadingText="Confirmando..."
          spinnerPlacement="end"
          isLoading={isExecuting}
          w={Utils.Responsive.DesktopPhone({ phone: '45%', desktop: 'auto' })}>
          Confirmar
        </Button>
      </HStack>
    </VStack>
  );
};
